import React from "react";
import { Box, FormControl, Typography } from "@mui/material";
import { FormattedMessage } from "react-intl";
import { InfoToolTipClick, InfoToolTipHover } from "../InfoTooltip";

/** Vertical rhythm shared by the field scaffold (label/control/error gap). */
const FORM_FIELD = {
  gap: "6px",
  labelFontSize: 14,
  labelFontWeight: 500,
  labelLineHeight: 1.57,
  errorFontSize: 12,
  errorFontWeight: 400,
  errorLineHeight: 1.67
};

/**
 * BaseFormField — the label + control + error scaffold shared by BaseTextField
 * and BaseDropdown.
 *
 * WHAT IT IS: the visual frame around a form control: a column-flex
 * `FormControl`, a `<label>` Typography (translated via `label` message id, with
 * an optional `*` and tooltip), the control itself (passed as `children`), and
 * an error region into which the field injects its `Collapse` rows.
 *
 * WHAT IT WRAPS: MUI `FormControl` / `Typography` / `Box` plus the shared
 * `InfoTooltip` widgets.
 *
 * CONTRACT (props in):
 *  - label: i18n message id; also used as the `htmlFor`/`id` of the control.
 *  - required: bool — sets FormControl.required and appends `*` to the label.
 *  - tooltip: { text, type, showOnAction: "click" | "hover" } | undefined —
 *      renders the matching InfoTooltip beside the label.
 *  - children: the control (Input/Select) — supplied by the field component.
 *  - errorContent: the field's `Collapse` error rows, rendered inside the
 *      error region (12/400).
 *
 * BEHAVIOR (out): consistent label/control/error layout. No validation logic
 * lives here — the field components own the rules and the Collapse conditions.
 *
 * MIGRATION NOTE (shadcn): this is the FormItem + FormLabel + FormMessage
 * grouping. `children` = the control slot, `errorContent` = `<FormMessage>`,
 * the label Typography = `<FormLabel>`. Mapping it later is mechanical.
 */
const BaseFormField = ({ label, required, tooltip, children, errorContent }) => {
  return (
    <FormControl
      required={required}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: FORM_FIELD.gap,
        width: "100%"
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "2px" }}>
        <Typography
          component="label"
          htmlFor={label}
          sx={{
            p: 0,
            fontSize: FORM_FIELD.labelFontSize,
            fontWeight: FORM_FIELD.labelFontWeight,
            lineHeight: FORM_FIELD.labelLineHeight,
            color: "var(--font-strong-default) !important"
          }}
        >
          <FormattedMessage id={label} />
          {required ? "*" : ""}
        </Typography>
        {tooltip && tooltip.showOnAction === "click" && (
          <InfoToolTipClick msg={tooltip.text} type={tooltip.type} testId={label} />
        )}
        {tooltip && tooltip.showOnAction === "hover" && (
          <InfoToolTipHover msg={tooltip.text} type={tooltip.type} testId={label} />
        )}
      </Box>

      {children}

      <Box
        sx={{
          fontSize: FORM_FIELD.errorFontSize,
          fontWeight: FORM_FIELD.errorFontWeight,
          lineHeight: FORM_FIELD.errorLineHeight,
          // Default colour for plain helper/optional messages; .text-error and
          // .text-success spans override it with their own colours.
          color: "var(--font-medium-default)"
        }}
      >
        {errorContent}
      </Box>
    </FormControl>
  );
};

export default BaseFormField;
